import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { ADMIN_API_URL } from "../../constants";

const CustomerDetailsPage = ({ addNotification }) => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loans, setLoans] = useState([]);
  const [kycDocuments, setKycDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      setError(null);
      const token = sessionStorage.getItem("authToken");

      if (!token) {
        setError("Authorization token not found.");
        addNotification("Authorization token not found. Please log in again.", 'error');
        setLoading(false);
        return;
      }

      try {
        // Fetch Customer Details
        const customerResponse = await fetch(`${ADMIN_API_URL}customers/detail/${customerId}`, {
          headers: { "Authorization": `${token}` }
        });
        if (!customerResponse.ok) {
          throw new Error('Failed to fetch customer details.');
        }
        const customerData = await customerResponse.json();
        setCustomer(customerData.data);

        // Fetch Customer Loans
        const loansResponse = await fetch(`${ADMIN_API_URL}loans/customer/${customerId}`, {
          headers: { "Authorization": `${token}` }
        });
        if (!loansResponse.ok) {
          throw new Error('Failed to fetch customer loans.');
        }
        const loansData = await loansResponse.json();
        setLoans(loansData.data);

        // Fetch KYC Documents
        const kycResponse = await fetch(`${ADMIN_API_URL}kyc/${customerId}`, {
          headers: { "Authorization": `${token}` }
        });
        if (!kycResponse.ok) {
          throw new Error('Failed to fetch KYC documents.');
        }
        const kycData = await kycResponse.json();
        setKycDocuments(kycData.data);

      } catch (err) {
        setError(err.message);
        addNotification(`Error fetching customer details: ${err.message}`, 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [customerId, addNotification]);

  const formatDateForDisplay = (isoDateString) => {
    if (!isoDateString) return 'N/A';
    const date = new Date(isoDateString);
    return date.toLocaleDateString(); // e.g., 6/13/2025
  };

  if (loading) {
    return (
      <>
        <Header />
        <div id="wrapper" style={{ paddingTop: '70px' }}>
          <div className="content">
            <p>Loading customer details...</p>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div id="wrapper" style={{ paddingTop: '70px' }}>
          <div className="content">
            <p className="text-danger">Error: {error}</p>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  if (!customer) {
    return (
      <>
        <Header />
        <div id="wrapper" style={{ paddingTop: '70px' }}>
          <div className="content">
            <p>No customer data available.</p>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div id="wrapper" style={{ paddingTop: '70px' }}>
        <div className="normalheader">
          <div className="hpanel">
            <div className="panel-body">
              <h2 className="font-light m-b-xs">Customer Details: {customer.name || customer.customerId}</h2>
            </div>
          </div>
        </div>

        <div className="content">
          <div className="row">
            <div className="col-lg-12">
              <div className="hpanel">
                <div className="panel-body">
                  <h4>Customer Information</h4>
                  <p><strong>Name:</strong> {customer.name || 'N/A'}</p>
                  <p><strong>Email:</strong> {customer.email || 'N/A'}</p>
                  <p><strong>Phone:</strong> {customer.mobile || 'N/A'}</p>
                  <p><strong>Address:</strong> {customer.address || 'N/A'}</p>
                  <p><strong>Customer ID:</strong> {customer.customerId || 'N/A'}</p>
                  <p><strong>Created At:</strong> {formatDateForDisplay(customer.createdAt)}</p>
                  
                  <hr />
                  <h4>Customer Loans</h4>
                  {loans.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Loan ID</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>Applied On</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loans.map(loan => (
                            <tr key={loan.id}>
                              <td>{loan.loanRequestId || 'N/A'}</td>
                              <td>{loan.loanAmount || 'N/A'}</td>
                              <td>{loan.status || 'N/A'}</td>
                              <td>{formatDateForDisplay(loan.createdAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p>No loans found for this customer.</p>
                  )}

                  <hr />
                  <h4>KYC Documents</h4>
                  {kycDocuments.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Document Type</th>
                            <th>Status</th>
                            <th>Uploaded On</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {kycDocuments.map(doc => (
                            <tr key={doc.id}>
                              <td>{doc.documentType || 'N/A'}</td>
                              <td>{doc.status || 'N/A'}</td>
                              <td>{formatDateForDisplay(doc.createdAt)}</td>
                              <td>
                                {doc.documentPath && (
                                  <button
                                    className="btn btn-xs btn-info"
                                    onClick={() => navigate(`/document-viewer/${encodeURIComponent(doc.documentPath)}`)}
                                  >
                                    View Document
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p>No KYC documents found for this customer.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default CustomerDetailsPage; 